import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as THREE from 'three';

const ParticleField = () => {
  const ref = useRef();
  const [sphere] = useMemo(() => {
    const sphere = new Float32Array(2000);
    for (let i = 0; i < 2000; i++) {
      const distance = Math.random() * 50 + 50;
      const theta = THREE.MathUtils.randFloatSpread(360);
      const phi = THREE.MathUtils.randFloatSpread(360);
      sphere[i * 3] = distance * Math.sin(theta) * Math.cos(phi);
      sphere[i * 3 + 1] = distance * Math.sin(theta) * Math.sin(phi);
      sphere[i * 3 + 2] = distance * Math.cos(theta);
    }
    return [sphere];
  }, []);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.rotation.x -= delta / 20;
      ref.current.rotation.y -= delta / 30;
    }
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled={false}>
        <PointMaterial 
          transparent 
          color="#00ff41" 
          size={0.5} 
          sizeAttenuation={true} 
          depthWrite={false} 
          opacity={0.4} 
        />
      </Points>
    </group>
  );
};

const DNAHelix = () => {
  const helixRef = useRef();
  const helixPoints = useMemo(() => {
    const points = [];
    const radius = 15;
    const height = 40;
    const turns = 4;
    
    for (let i = 0; i < 200; i++) {
      const t = (i / 200) * turns * Math.PI * 2;
      const y = (i / 200) * height - height / 2;
      
      // First strand
      points.push(
        radius * Math.cos(t),
        y,
        radius * Math.sin(t)
      );
      
      // Second strand (opposite)
      points.push(
        radius * Math.cos(t + Math.PI),
        y,
        radius * Math.sin(t + Math.PI)
      );
    }
    return new Float32Array(points);
  }, []);

  useFrame((state) => {
    if (helixRef.current) {
      helixRef.current.rotation.y = state.clock.elapsedTime * 0.3;
    }
  });

  return (
    <group ref={helixRef} position={[20, 0, -20]}>
      <Points positions={helixPoints} stride={3} frustumCulled={false}>
        <PointMaterial 
          transparent 
          color="#39ff14" 
          size={0.8} 
          sizeAttenuation={true} 
          depthWrite={false} 
          opacity={0.5} 
        />
      </Points>
    </group>
  );
};

const NeuralNetwork = () => {
  const networkRef = useRef();
  const networkPoints = useMemo(() => {
    const points = [];
    const nodes = 30;
    
    for (let i = 0; i < nodes; i++) {
      const x = (Math.random() - 0.5) * 60;
      const y = (Math.random() - 0.5) * 60;
      const z = (Math.random() - 0.5) * 60;
      points.push(x, y, z);
    }
    return new Float32Array(points);
  }, []);

  useFrame((state) => {
    if (networkRef.current) {
      networkRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.2) * 0.1;
      networkRef.current.rotation.z = Math.cos(state.clock.elapsedTime * 0.15) * 0.1;
    }
  });

  return (
    <group ref={networkRef} position={[-25, 0, 10]}>
      <Points positions={networkPoints} stride={3} frustumCulled={false}>
        <PointMaterial 
          transparent 
          color="#00ff00" 
          size={1.2} 
          sizeAttenuation={true} 
          depthWrite={false} 
          opacity={0.6} 
        />
      </Points>
    </group>
  );
};

const MatrixRain = () => {
  const rainRef = useRef();
  const matrixPoints = useMemo(() => {
    const points = [];
    const drops = 150;
    
    for (let i = 0; i < drops; i++) {
      const x = (Math.random() - 0.5) * 200;
      const y = Math.random() * 100 + 50;
      const z = (Math.random() - 0.5) * 100;
      points.push(x, y, z);
    }
    return new Float32Array(points);
  }, []);

  useFrame((state) => {
    if (rainRef.current) {
      const positions = rainRef.current.geometry.attributes.position.array;
      for (let i = 1; i < positions.length; i += 3) {
        positions[i] -= 0.5; // Fall speed
        if (positions[i] < -50) {
          positions[i] = 100; // Reset to top
        }
      }
      rainRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <Points ref={rainRef} positions={matrixPoints} stride={3} frustumCulled={false}>
      <PointMaterial 
        transparent 
        color="#00ff41" 
        size={0.3} 
        sizeAttenuation={true} 
        depthWrite={false} 
        opacity={0.8} 
      />
    </Points>
  );
};

const Background3D = () => {
  return (
    <div className="fixed inset-0 -z-10">
      <Canvas 
        camera={{ position: [0, 0, 80], fov: 60 }}
        style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 25%, #001100 50%, #0a0a0a 75%, #000000 100%)' 
        }}
      >
        <ambientLight intensity={0.3} />
        <pointLight position={[10, 10, 10]} color="#00ff00" intensity={0.5} />
        <ParticleField />
        <DNAHelix />
        <NeuralNetwork />
        <MatrixRain />
      </Canvas>
      
      {/* Matrix overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-transparent to-black/40 pointer-events-none" />
      
      {/* Animated grid overlay with green matrix effect */}
      <div 
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,255,65,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,255,65,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '30px 30px',
          animation: 'matrixGrid 15s linear infinite'
        }}
      />
      
      {/* Scan lines effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="w-full h-0.5 bg-gradient-to-r from-transparent via-green-400/60 to-transparent absolute"
          style={{ animation: 'scanLine 4s ease-in-out infinite' }}
        />
      </div>
    </div>
  );
};

export default Background3D;